import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes, css } from "styled-components";
import { observer } from "mobx-react-lite";
import timerGif from "../../../../assets/images/timer.gif";
import { useStores, useTheme, useResponsive } from "hooks";
import AudioSettingsModal from "components/project/BidPanel/AudioSettingsModal";

// Configuration constants
const OUTBID_FLASH_THRESHOLD = 10; // seconds remaining when outbid flashing starts

// Add a new animation for the outbid notification
const flashAnimation = keyframes`
    0%, 100% { background-color: rgba(231, 76, 60, 0.2); }
    50% { background-color: rgba(231, 76, 60, 0.3); }
`;

const shakeAnimation = keyframes`
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
    20%, 40%, 60%, 80% { transform: translateX(5px); }
`;

// Modify the Container to include the outbid animation with conditional timing
const Container = styled.div`
    display: flex;
    flex-direction: column;
    background-color: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    padding: 5px 12px;

    position: relative;
    overflow: hidden;
    height: 100%;

    /* Create a consistent layout with fixed sections */
    display: grid;
    grid-template-rows: auto auto 1fr auto;
    box-sizing: border-box;
    width: 100%;

    ${({ isWatched }) =>
            isWatched &&
            css`
                background-color: #f0f8ff;
                border-left: 3px solid #4a90e2;
            `}

    ${({ isOutbid, timeRemaining, flashThreshold }) =>
            isOutbid &&
            timeRemaining <= flashThreshold &&
            css`
                animation: ${flashAnimation} 1.5s ease-in-out infinite;
            `}
`;

// Add a new OutbidNotification component
const OutbidNotification = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: #e74c3c;
    color: white;
    padding: 4px;
    text-align: center;
    font-weight: bold;
    z-index: 10;
    animation: ${shakeAnimation} 0.5s ease-in-out;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const OutbidNotificationText = styled.h3`
    font-size: 12px;
    font-weight: normal;
    margin: 0;
    color: white;
`;


// Add a pulse animation for the button and status text
const pulse = keyframes`
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
`;

const StatusText = styled.div`
    font-size: 12px;
    font-weight: 600;
    text-align: center;
    margin-bottom: 4px;
    color: ${({ type, customColor }) => {
        if (customColor) return customColor;

        switch (type) {
            case "watching":
                return "#3498db";
            case "winning":
                return "#2ecc71";
            case "outbid":
                return "#e74c3c";
            default:
                return "#666";
        }
    }};
    background-color: ${({ customBgColor }) => customBgColor || "transparent"};
    padding: ${({ customBgColor }) => customBgColor ? "4px 8px" : "0"};
    border-radius: ${({ customBgColor }) => customBgColor ? "4px" : "0"};

    ${({ type }) =>
            type === "outbid" &&
            css`
                animation: ${pulse} 1.5s infinite;
            `}
`;

// Modify the BidButton to highlight when outbid
const BidButton = styled.button`
    width: 100%;
    padding: 8px;
    background-color: ${({ isBonusTime, isOutbid }) =>
            isOutbid ? "#e74c3c" : isBonusTime ? "#f39c12" : "#2ecc71"};
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
        background-color: ${({ isBonusTime, isOutbid }) =>
                isOutbid ? "#c0392b" : isBonusTime ? "#e67e22" : "#27ae60"};
    }

    &:disabled {
        background-color: #bdc3c7;
        cursor: not-allowed;
        animation: none;
        box-shadow: none;
    }
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 4px;
`;

const ProductName = styled.h3`
    margin: 2px 0 0 0;
    font-size: 13px;
    font-weight: 600;
    flex: 1;
    padding-right: 8px;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.3;
`;

const BidInfoGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4px;
    margin-bottom: 6px;
    min-width: 0;
`;

const BidInfoItem = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
`;

const Label = styled.span`
    font-size: 11px;
    color: #666;
    margin-bottom: 1px;
`;

const Value = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: #333;

    ${({ highlight }) =>
            highlight &&
            css`
                color: #e74c3c;
            `}
`;

const HighBidder = styled(Value)`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    overflow-wrap: anywhere;
`;

// Timer animation
const countdown = keyframes`
    from { stroke-dashoffset: 0; }
    to { stroke-dashoffset: 283; } /* 2 * PI * 45 */
`;

const TimerSection = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-grow: 1;
    margin: -8px 0 0 0;
    height: 60px;
`;

const TimerWrapper = styled.div`
    position: relative;
    width: 55px;
    height: 55px;
`;

const TimerSvg = styled.svg`
    transform: rotate(-90deg);
    width: 100%;
    height: 100%;
`;

const TimerBackground = styled.circle`
    fill: none;
    stroke: #e0e0e0;
    stroke-width: 5;
`;

const TimerProgress = styled.circle`
    fill: none;
    stroke: ${({ isBonusTime, progress, currentTime }) => {
        if (isBonusTime) return currentTime <= 5 ? "#e74c3c" : "#f39c12";

        // Color gradient based on progress (0 to 1)
        if (progress >= 0.6) return "#2ecc71"; // Green for > 60%
        if (progress >= 0.3) return "#f39c12"; // Orange for 30-60%
        return "#e74c3c"; // Red for < 30%
    }};
    stroke-width: 5;
    stroke-dasharray: 283; /* 2 * PI * 45 */
    stroke-linecap: round;

    animation: ${({ isActive, duration }) =>
            isActive &&
            css`
                ${countdown} ${duration}s linear forwards
            `};
`;

const TimerText = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 12px;
    font-weight: 700;
    color: #333;
`;

const TimerGif = styled.img`
    border: 0;
    width: 70px;
    height: 70px;
    object-fit: contain;
`;

const BottomSection = styled.div`
    position: relative;
    margin-top: auto;
    margin-bottom: 8px;
    display: flex;
    flex-direction: column;
`;

const AudioSettingsIconButton = styled.button`
    background: none;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    padding: 4px 6px;
    cursor: pointer;
    color: #666;
    font-size: 13px;
    line-height: 1;
    flex-shrink: 0;

    &:hover {
        background: #f5f5f5;
        color: #333;
    }
`;

// Create a ref to track which lot numbers have already played the outbid sound
// Using a module-level variable so it persists across component instances
const outbidSoundPlayedForLots = new Set();

export const ElaneBidDetails = ({
                                    productName,
                                    highBidder,
                                    highBid,
                                    nextBid,
                                    timeRemaining,
                                    isWatched,
                                    isBonusTime,
                                    isCountdownActive,
                                    isDisabled,
                                    onBid,
                                    decisionStatus,
                                    lotNumber,
                                    bidButtonText = "Bid",
                                    outbidFlashThreshold = OUTBID_FLASH_THRESHOLD, // Default value
                                    winningBidderName,
                                    currentBidder,
                                    auctionId,
                                    laneID,
                                }) => {
    const { VirtualAuctionStore, MultiAuctionStore } = useStores(auctionId);
    const { bidStatusMessage, bidStatusCode, playVoiceOver, playEffect } =
        VirtualAuctionStore;
    const { user } = MultiAuctionStore;
    const { isMobileView } = useResponsive();

    const [animationKey, setAnimationKey] = useState(0);
    const [currentTime, setCurrentTime] = useState(timeRemaining);
    const [progress, setProgress] = useState(1);
    const [showOutbidNotification, setShowOutbidNotification] = useState(false);
    const [bidLock, setBidLock] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);

    const isVirtualLaneAudio = VirtualAuctionStore.auction[laneID]?.audioPlaying && VirtualAuctionStore.auction[laneID]?.isVirtual;
    const isAuctionFinished = !!(decisionStatus && decisionStatus.message);

    // Ref to track the previous status code to trigger sfx only on change
    const prevStatusCodeRef = useRef(bidStatusCode);

    // Ref to track the current lot number
    const currentLotRef = useRef(lotNumber);

    const remainingFromStore =
        typeof VirtualAuctionStore.getRemainingTime === "function"
              ? VirtualAuctionStore.getRemainingTime()
              : currentTime;

    const clientClosing = (isCountdownActive || isBonusTime) && remainingFromStore <= 0.5;

    const buttonEnabled = isCountdownActive && !isAuctionFinished && !isDisabled && !bidLock;


    const handleBid = () => {
        if (!buttonEnabled) return;
        setBidLock(true);
        try {
            onBid();
        } finally {
            setTimeout(() => setBidLock(false), 800); // simple debounce
        }
    };

    // --- SIMPLIFIED LOGIC ---
    // Use name comparison for winning status (reliable for all bid types)
    // Only show outbid status when server explicitly confirms it

    // Always use name comparison for winning (most reliable)
    const isWinning = user.name === winningBidderName;

    // Only show as outbid if NOT currently winning
    // bidStatusCode is unreliable for proxy bids
    const isOutbid = !isWinning && (bidStatusCode === 0 || bidStatusCode === 1);

    // Reset the outbid sound played flag when lot number changes
    useEffect(() => {
        if (lotNumber !== currentLotRef.current) {
            setShowOutbidNotification(false)
            currentLotRef.current = lotNumber;
        }
    }, [lotNumber]);

    // Show notification and play sound only when status changes to 0
    useEffect(() => {
        // Condition: bidStatusCode is 0, AND its previous state was not 0.
        if (bidStatusCode === 0 && prevStatusCodeRef.current !== 0) {
            setShowOutbidNotification(true);

            // Only play the sound if we haven't played it for this lot yet
            if (lotNumber && !outbidSoundPlayedForLots.has(lotNumber)) {
                if (VirtualAuctionStore.soundPrefs.voiceOutbid) {
                    playVoiceOver("youve_been_outbid.mp3");
                }
                outbidSoundPlayedForLots.add(lotNumber);
            }
            else {
                if (VirtualAuctionStore.soundPrefs.outbidAlert) {
                    playEffect('outbid_sfx.mp3');
                }
            }

            const timer = setTimeout(() => {
                setShowOutbidNotification(false);
            }, 3000);

            return () => clearTimeout(timer);
        }

        // Update the ref for the next render *after* the check.
        if (prevStatusCodeRef.current !== bidStatusCode) {
            prevStatusCodeRef.current = bidStatusCode;
        }
    }, [bidStatusCode, lotNumber]);

    // Reset animation when countdown resets
    useEffect(() => {
        if (isCountdownActive) {
            setAnimationKey((prev) => prev + 1);
            setCurrentTime(timeRemaining);
            setProgress(1);
        }
    }, [isCountdownActive, timeRemaining, isBonusTime]);

    // Countdown timer effect
    useEffect(() => {
        let timer;

        if (isCountdownActive && timeRemaining > 0) {
            setCurrentTime(timeRemaining);

            timer = setInterval(() => {
                setCurrentTime((prevTime) => {
                    const newTime = Math.max(0, prevTime - 0.1);
                    setProgress(newTime / timeRemaining);
                    return newTime;
                });
            }, 100);
        }

        return () => {
            if (timer) clearInterval(timer);
        };
    }, [isCountdownActive, timeRemaining]);

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    // Determine status type for styling based on the code
    const getStatusType = () => {
        if (isOutbid) return "outbid";
        if (isWinning) return "winning";
        if (isWatched) return "watching";
        return null;
    };

    // Format time remaining
    const formatTimeRemaining = (seconds) => {
        if (seconds === undefined || seconds === null) return "";
        return `${Math.ceil(seconds)}s`;
    };

    const showAnimation =
        bidButtonText.toLowerCase().includes("paused") ||
        bidButtonText.toLowerCase().includes("closing") ||
        bidButtonText.toLowerCase().includes("waiting");

    // Modify bid button text when outbid
    const displayBidButtonText = isOutbid
        ? `Bid Now! ${formatCurrency(nextBid)}`
        : bidButtonText;

    const statusType = getStatusType();

    // Generate our own status message based on current state (not stale server message)
    const getStatusMessage = () => {
        if (isWinning) return "Current High Bidder";
        if (isOutbid) return "You have been outbid";
        if (isWatched) return "Watching";
        return null;
    };

    return (
        <Container
            isWatched={isWatched}
            isOutbid={isOutbid}
            timeRemaining={currentTime}
            flashThreshold={outbidFlashThreshold}
        >
            {showOutbidNotification && (
                <OutbidNotification>
                    <OutbidNotificationText>You've been outbid!</OutbidNotificationText>
                </OutbidNotification>
            )}

            <Header>
                <ProductName>{productName}</ProductName>
                {isVirtualLaneAudio && isMobileView && (
                    <AudioSettingsIconButton
                        onClick={() => setSettingsOpen(true)}
                        aria-label="Audio settings"
                    >
                        <i className="fa fa-cog" />
                    </AudioSettingsIconButton>
                )}
            </Header>

            <AudioSettingsModal isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} />

            <BidInfoGrid>
                <BidInfoItem>
                    <Label>High Bidder</Label>
                    <HighBidder highlight={isWinning || isOutbid}>
                        {highBidder || "No bids yet"}
                    </HighBidder>
                </BidInfoItem>

                <BidInfoItem>
                    <Label>Current Bid</Label>
                    <Value highlight={isOutbid}>
                        {highBid ? formatCurrency(highBid) : "N/A"}
                    </Value>
                </BidInfoItem>
            </BidInfoGrid>

            <TimerSection>
                {timeRemaining !== undefined &&
                timeRemaining !== null &&
                !showAnimation &&
                (isCountdownActive || isBonusTime) ? (
                    <TimerWrapper>
                        <TimerSvg key={animationKey} viewBox="0 0 100 100">
                            <TimerBackground cx="50" cy="50" r="45" />
                            <TimerProgress
                                cx="50"
                                cy="50"
                                r="45"
                                isActive={isCountdownActive}
                                duration={timeRemaining}
                                isBonusTime={isBonusTime}
                                progress={progress}
                                currentTime={currentTime}
                            />
                        </TimerSvg>
                        <TimerText>{formatTimeRemaining(currentTime)}</TimerText>
                    </TimerWrapper>
                ) : (
                    <TimerGif src={timerGif} />
                )}
            </TimerSection>

            <BottomSection>
                {isAuctionFinished ? (
                    <StatusText
                        customColor={decisionStatus.color}
                        customBgColor={decisionStatus.bgColor}
                    >
                        {decisionStatus.message}
                    </StatusText>
                ) : (
                    statusType && getStatusMessage() && (
                        <StatusText type={statusType}>{getStatusMessage()}</StatusText>
                    )
                )}
                <BidButton
                    onClick={handleBid}
                    disabled={!buttonEnabled}
                    isBonusTime={isBonusTime}
                    isOutbid={isOutbid}
                >
                    {isAuctionFinished
                        ? "Auction Closed"
                        : !isCountdownActive
                            ? "Waiting"
                            : displayBidButtonText}
                </BidButton>

            </BottomSection>
        </Container>
    );
};

export default observer(ElaneBidDetails);
